<template>
  <div class="relative w-full h-full p-0 border-0" :class="classes">
    <!-- Conditionally render textarea or HTML based on type and autoHtml -->
    <textarea
      v-if="!shouldRenderAsHtml"
      :id="name"
      ref="textareaRef"
      v-model="inputValue"
      :disabled="disabled"
      :invalid="meta.invalid"
      :valid="meta.valid && meta.dirt"
      :rows="rows"
      :name="name"
      class="w-full peer form-input dark:bg-dark-box-background pb-0"
      :style="textareaStyle"
      :class="[{ 'border-status-error-red text-status-error-red': errorMessage, 'pb-10': showCounter }, inputClasses]"
      :placeholder="placeholder"
      @input="onInputChange"
      @change="handleChange"
      @blur="onBlur" />
    <!-- HTML display mode with double-click to edit -->
    <div
      v-else-if="!isEditingHtml"
      class="w-full p-2 dark:bg-dark-box-background cursor-pointer"
      @dblclick="enterEditMode"
      v-html="sanitizedHtml" />

    <!-- HTML editing mode -->
    <textarea
      v-else
      :id="name + '_html_edit'"
      ref="htmlEditRef"
      v-model="inputValue"
      :disabled="disabled"
      :rows="rows"
      class="w-full peer form-input dark:bg-dark-box-background pb-0"
      :style="textareaStyle"
      :class="[{ 'border-status-error-red text-status-error-red': errorMessage, 'pb-10': showCounter }, inputClasses]"
      @blur="handleExitEditMode"
      @keydown.esc="handleExitEditMode" />

    <label
      v-show="showLabel"
      :class="{ 'peer-placeholder-shown:text-status-error-red': errorMessage }"
      :for="name"
      class="absolute left-1 rounded-md px-1 -top-3.5 text-gray-600 text-md transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-2 peer-focus:-top-3.5 text-base font-medium dark:text-dark-body-text peer-focus:font-normal peer-focus:text-blue bg-light-label-background dark:bg-dark-box-background">{{ label }}</label>

    <div v-show="errorMessage" class="absolute inset-y-0 right-0 z-10 flex items-center pr-3 pointer-events-none">
      <CircleX class="w-5 h-5 text-red-400" />
    </div>
    <p class="absolute top-0 text-red-400 right-1 first-letter:capitalize text-xsm" :class="[errorMessage ? 'visible' : 'invisible']">
      {{ errorMessage }}
    </p>
    <!-- counter -->
    <div v-show="showCounter" class="absolute z-10 flex items-center pointer-events-none bottom-4 left-4">
      <span class="text-gray-500 dark:text-dark-body-text">{{ inputValue?.length }}</span>
      <span v-if="maxLength" class="text-gray-400 dark:text-dark-body-text">/</span>
      <span v-if="maxLength" class="text-gray-500 dark:text-dark-body-text">{{ maxLength }}</span>
    </div>
  </div>
</template>

<script>
import { toRef, computed, ref } from "vue"
import { useField } from "vee-validate"
import { CircleX } from "lucide-vue-next"
import { utils } from "@/store/helpers"
import DOMPurify from "dompurify"

export default {
  components: {
    CircleX
  },
  props: {
    disabled: null,
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    value: {
      type: [String, Number],
      default: null
    },
    modelValue: {
      type: [String, Number],
      default: null
    },
    placeholder: {
      type: String,
      default: ""
    },
    classes: {
      type: String,
      default: ""
    },
    inputClasses: {
      type: String,
      default: ""
    },
    rows: {
      type: Number,
      default: 4
    },
    validateOnInput: {
      type: Boolean,
      default: false
    },
    showCounter: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: null
    },
    maxHeight: {
      type: String,
      default: "200px"
    },
    debounce: {
      type: Number,
      default: 0
    },
    type: {
      type: String,
      default: "text" // or "html"
    },
    autoHtml: {
      type: Boolean,
      default: true
    }
  },
  emits: ["update:modelValue"],
  setup(props) {
    const name = toRef(props, "name")
    const textareaRef = ref(null)
    const isEditingHtml = ref(false)
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      meta
    } = useField(name, undefined, {
      initialValue: props.value || props.modelValue
    })

    // Detect if the input contains HTML tags
    const isHtmlContent = computed(() => {
      const htmlPattern = /<\/?[a-z][\s\S]*>/i
      return htmlPattern.test(inputValue.value)
    })

    // Determine if HTML should be rendered based on type and autoHtml prop
    const shouldRenderAsHtml = computed(() => {
      return props.type === "html" || (props.autoHtml && isHtmlContent.value)
    })

    const sanitizedHtml = computed(() => DOMPurify.sanitize(inputValue.value))

    // Computed style for textarea with dynamic height and max-height
    const textareaStyle = computed(() => {
      return {
        resize: "none", // Disable manual resizing
        overflowY: "auto", // Scrollbar will appear if content exceeds maxHeight or minHeight isn't met by content
        maxHeight: props.maxHeight,
        minHeight: props.rows === 1 ? "38px" : "auto" // Set minimum height for single line inputs
      }
    })

    // Methods for HTML editing
    const htmlEditRef = ref(null)

    // Adjust textarea height function
    const adjustTextareaHeight = (textarea) => {
      if (!textarea) {
        return
      }

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto"

      // Set the height to the scrollHeight to expand based on content
      // The max-height constraint will be applied via the textareaStyle binding
      textarea.style.height = `${textarea.scrollHeight}px`
    }

    const enterEditMode = () => {
      isEditingHtml.value = true
      // Focus the textarea after it's rendered
      setTimeout(() => {
        if (htmlEditRef.value) {
          htmlEditRef.value.focus()
          // Adjust height after focusing
          adjustTextareaHeight(htmlEditRef.value)
        }
      }, 0)
    }

    const exitEditMode = () => {
      isEditingHtml.value = false
      // Sanitize the HTML content when exiting edit mode
      inputValue.value = DOMPurify.sanitize(inputValue.value)

      // Return the updated value so it can be emitted by the calling context
      return inputValue.value
    }

    return {
      textareaStyle,
      errorMessage,
      inputValue,
      handleBlur,
      handleChange,
      meta,
      sanitizedHtml,
      shouldRenderAsHtml,
      textareaRef,
      isEditingHtml,
      htmlEditRef,
      enterEditMode,
      exitEditMode,
      adjustTextareaHeight
    }
  },
  watch: {
    value() {
      this.inputValue = this.value
    },
    modelValue() {
      this.inputValue = this.modelValue
    }
  },
  mounted() {
    // Initial height adjustment
    this.$nextTick(this.adjustTextareaHeightMethod)

    // Watch for value changes to adjust height
    this.$watch("inputValue", () => {
      this.$nextTick(this.adjustTextareaHeightMethod)
    })
  },
  methods: {
    onInputChange(e) {
      if (this.validateOnInput) {
        utils.debounce(() => {
          this.handleChange(e)
          if (this.validateOnInput) {
            this.$emit("update:modelValue", this.inputValue)
          }
        }, this.debounce)
      }

      // Auto-resize the textarea
      this.$nextTick(this.adjustTextareaHeightMethod)
    },
    onBlur(e) {
      this.handleBlur(e)
      this.$emit("update:modelValue", this.inputValue)
    },
    adjustTextareaHeightMethod() {
      // Use the setup function's adjustTextareaHeight
      this.adjustTextareaHeight(this.textareaRef)
    },
    handleExitEditMode() {
      // Call the setup function's exitEditMode and emit the result
      const updatedValue = this.exitEditMode()
      this.$emit("update:modelValue", updatedValue)
    }
  }
}
</script>
